import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CreateInsuranceAgencyPayload,
  InsuranceAgencyListItem,
  InsuranceApiService,
  InsuranceChannelBinding,
  InsuranceProducerListItem,
  InsuranceTerritory,
  InsuranceProductListItem,
  UpsertInsuranceChannelBindingPayload
} from './insurance-api.service';
import { SalesApiService, SalesChannelRecord } from '../sales/sales-api.service';

type DistributionAdminTab = 'agencies' | 'channelBindings';

@Component({
  standalone: true,
  selector: 'ain-insurance-distribution-admin',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    PartySelectorComponent,
    FormsModule,
    TableModule,
    SelectModule,
    MultiSelectModule,
    InputTextModule,
    InputNumberModule,
    ButtonModule,
    CheckboxModule,
    DialogModule
  ],
  template: `
    <ain-page
      [title]="t('insurance.distributionAdmin.title')"
      [description]="t('insurance.distributionAdmin.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.distribution.help.page.title'"
        [descriptionKey]="'insurance.distribution.help.page.description'"
        [bulletKeys]="[
          'insurance.distribution.help.page.bullets.agencies',
          'insurance.distribution.help.page.bullets.channels',
          'insurance.distribution.help.page.bullets.hierarchy'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('insurance.states.noPermission')" [message]="t('insurance.states.noPermissionMessage')" />
      } @else {
        <section class="card panel">
          <div class="toolbar tabs">
            <button
              pButton
              type="button"
              [severity]="activeTab() === 'agencies' ? undefined : 'secondary'"
              [label]="t('insurance.distributionAdmin.tabs.agencies')"
              (click)="activeTab.set('agencies')"></button>
            <button
              pButton
              type="button"
              [severity]="activeTab() === 'channelBindings' ? undefined : 'secondary'"
              [label]="t('insurance.distributionAdmin.tabs.channelBindings')"
              (click)="activeTab.set('channelBindings')"></button>
          </div>

          @if (activeTab() === 'agencies') {
            <div class="toolbar">
              <button pButton type="button" [label]="t('insurance.distributionAdmin.agencies.actions.create')" (click)="openAgencyDialog()"></button>
              <button pButton type="button" severity="secondary" [label]="t('insurance.actions.refresh')" (click)="loadAgencies()"></button>
            </div>

            <details class="filters" [open]="true">
              <summary>
                <span>{{ t('insurance.filters.title') }}</span>
                <ain-info-hint
                  [titleKey]="'insurance.distribution.help.agencies.title'"
                  [descriptionKey]="'insurance.distribution.help.agencies.description'"
                  [bulletKeys]="[
                    'insurance.distribution.help.agencies.bullets.search',
                    'insurance.distribution.help.agencies.bullets.parent',
                    'insurance.distribution.help.agencies.bullets.create'
                  ]" />
              </summary>
              <div class="filter-grid">
                <label>
                  <span>{{ t('insurance.distributionAdmin.agencies.filters.search') }}</span>
                  <input pInputText [value]="agencySearch()" (input)="onAgencySearch($event)" />
                </label>
              </div>
            </details>

            @if (filteredAgencyRows().length === 0) {
              <ain-state type="empty" [title]="t('insurance.distributionAdmin.agencies.states.empty')" [message]="t('insurance.distributionAdmin.agencies.states.emptyMessage')" />
            } @else {
              <p-table [value]="filteredAgencyRows()" dataKey="code" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.distributionAdmin.agencies.fields.code') }}</th>
                    <th>{{ t('insurance.distributionAdmin.agencies.fields.name') }}</th>
                    <th>{{ t('insurance.distributionAdmin.agencies.fields.parentAgency') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.code }}</td>
                    <td>{{ row.displayName }}</td>
                    <td>{{ row.parentName }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          } @else {
            <div class="toolbar">
              <button pButton type="button" severity="secondary" [label]="t('insurance.distributionAdmin.channelBindings.actions.load')" [disabled]="!selectedChannelId" (click)="loadChannelBinding()"></button>
              <button pButton type="button" [label]="t('insurance.distributionAdmin.channelBindings.actions.configure')" [disabled]="!selectedChannelId" (click)="openChannelBindingDialog()"></button>
            </div>

            <details class="filters" [open]="true">
              <summary>
                <span>{{ t('insurance.filters.title') }}</span>
                <ain-info-hint
                  [titleKey]="'insurance.distribution.help.channelBindings.title'"
                  [descriptionKey]="'insurance.distribution.help.channelBindings.description'"
                  [bulletKeys]="[
                    'insurance.distribution.help.channelBindings.bullets.select',
                    'insurance.distribution.help.channelBindings.bullets.products',
                    'insurance.distribution.help.channelBindings.bullets.territory'
                  ]" />
              </summary>
              <div class="filter-grid">
                <label>
                  <span>{{ t('insurance.distributionAdmin.channelBindings.fields.salesChannel') }}</span>
                  <p-select
                    [options]="channelOptions()"
                    optionLabel="label"
                    optionValue="value"
                    [(ngModel)]="selectedChannelId"
                    name="selectedChannelId"
                    [showClear]="true"
                    [filter]="true"
                    appendTo="body" />
                </label>
              </div>
            </details>

            @if (loadedBinding()) {
              <div class="binding-summary">
                <h4>{{ t('insurance.distributionAdmin.channelBindings.summary.title') }}</h4>
                <div class="summary-grid">
                  <p><strong>{{ t('insurance.distributionAdmin.channelBindings.fields.defaultProducer') }}:</strong> {{ producerLabel(loadedBinding()!.defaultProducerId) }}</p>
                  <p><strong>{{ t('insurance.distributionAdmin.channelBindings.fields.quoteValidityHours') }}:</strong> {{ loadedBinding()!.quoteValidityHours }}</p>
                  <p><strong>{{ t('insurance.distributionAdmin.channelBindings.fields.territory') }}:</strong> {{ territoryLabel(loadedBinding()!.territoryId) }}</p>
                  <p><strong>{{ t('insurance.distributionAdmin.channelBindings.fields.territoryMode') }}:</strong> {{ territoryModeLabel(loadedBinding()!.territoryEnforcementMode) }}</p>
                  <p><strong>{{ t('insurance.distributionAdmin.channelBindings.fields.customerConflictMode') }}:</strong> {{ customerConflictModeLabel(loadedBinding()!.customerConflictMode) }}</p>
                  <p><strong>{{ t('insurance.distributionAdmin.channelBindings.fields.requiresUwApproval') }}:</strong> {{ loadedBinding()!.requiresUwApproval ? t('common.yes') : t('common.no') }}</p>
                  <p class="wide"><strong>{{ t('insurance.distributionAdmin.channelBindings.fields.allowedProducts') }}:</strong> {{ allowedProductsSummary(loadedBinding()!.allowedProductIdsJson) }}</p>
                </div>
              </div>
            } @else if (selectedChannelId) {
              <ain-state type="empty" [title]="t('insurance.distributionAdmin.channelBindings.states.notLoaded')" [message]="t('insurance.distributionAdmin.channelBindings.states.notLoadedMessage')" />
            } @else {
              <ain-state type="empty" [title]="t('insurance.distributionAdmin.channelBindings.states.noChannel')" [message]="t('insurance.distributionAdmin.channelBindings.states.noChannelMessage')" />
            }
          }

          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }

      <p-dialog
        [header]="t('insurance.distributionAdmin.agencies.dialog.create')"
        [modal]="true"
        [(visible)]="agencyDialogVisible"
        [style]="{ width: '640px' }">
        <div class="dialog-grid">
          <label class="wide">
            <span>{{ t('insurance.distributionAdmin.agencies.fields.legalEntityId') }}</span>
            <ain-party-selector [(ngModel)]="agencyForm.legalEntityId" name="agencyLegalEntityId" />
          </label>
          <label>
            <span>{{ t('insurance.distributionAdmin.agencies.fields.code') }}</span>
            <input pInputText [(ngModel)]="agencyForm.code" name="agencyCode" />
          </label>
          <label>
            <span>{{ t('insurance.distributionAdmin.agencies.fields.name') }}</span>
            <input pInputText [(ngModel)]="agencyForm.name" name="agencyName" />
          </label>
          <label>
            <span>{{ t('insurance.distributionAdmin.agencies.fields.parentAgency') }}</span>
            <p-select
              [options]="agencyOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="agencyForm.parentAgencyId"
              name="agencyParentId"
              [showClear]="true"
              [filter]="true"
              appendTo="body" />
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="agencyDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveAgency()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog
        [header]="t('insurance.distributionAdmin.channelBindings.dialog.configure')"
        [modal]="true"
        [(visible)]="channelBindingDialogVisible"
        [style]="{ width: '760px' }">
        <div class="dialog-grid">
          <label class="wide">
            <span>{{ t('insurance.distributionAdmin.channelBindings.fields.legalEntityId') }}</span>
            <ain-party-selector [(ngModel)]="channelBindingForm.legalEntityId" name="bindingLegalEntityId" />
          </label>
          <label>
            <span>{{ t('insurance.distributionAdmin.channelBindings.fields.defaultProducer') }}</span>
            <p-select
              [options]="producerOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="channelBindingForm.defaultProducerId"
              name="bindingProducerId"
              [showClear]="true"
              [filter]="true"
              appendTo="body" />
          </label>
          <label class="wide">
            <span>{{ t('insurance.distributionAdmin.channelBindings.fields.allowedProducts') }}</span>
            <p-multiSelect
              [options]="productOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="selectedAllowedProductIds"
              name="bindingAllowedProductIds"
              [filter]="true"
              [showClear]="true"
              appendTo="body" />
          </label>
          <label>
            <span>{{ t('insurance.distributionAdmin.channelBindings.fields.quoteValidityHours') }}</span>
            <p-inputNumber [(ngModel)]="channelBindingForm.quoteValidityHours" name="bindingQuoteValidityHours" [min]="1" [max]="8760" />
          </label>
          <label>
            <span>{{ t('insurance.distributionAdmin.channelBindings.fields.territory') }}</span>
            <p-select
              [options]="territoryOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="channelBindingForm.territoryId"
              name="bindingTerritoryId"
              [showClear]="true"
              [filter]="true"
              appendTo="body" />
          </label>
          <label>
            <span>{{ t('insurance.distributionAdmin.channelBindings.fields.territoryMode') }}</span>
            <p-select
              [options]="territoryModeOptions"
              optionValue="value"
              [(ngModel)]="channelBindingForm.territoryEnforcementMode"
              name="bindingTerritoryMode"
              appendTo="body">
              <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '' }}</ng-template>
              <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
            </p-select>
          </label>
          <label>
            <span>{{ t('insurance.distributionAdmin.channelBindings.fields.customerConflictMode') }}</span>
            <p-select
              [options]="customerConflictModeOptions"
              optionValue="value"
              [(ngModel)]="channelBindingForm.customerConflictMode"
              name="bindingConflictMode"
              appendTo="body">
              <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '' }}</ng-template>
              <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
            </p-select>
          </label>
          <label class="checkbox wide">
            <p-checkbox binary="true" [(ngModel)]="channelBindingForm.requiresUwApproval" name="bindingRequiresUwApproval" />
            <span>{{ t('insurance.distributionAdmin.channelBindings.fields.requiresUwApproval') }}</span>
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="channelBindingDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving() || !selectedChannelId" (click)="saveChannelBinding()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .tabs { margin-bottom: 0; }
    .filters { border: 1px solid #e5e7eb; border-radius: 10px; padding: 8px 10px; background: #f9fafb; }
    .filters summary { display: flex; align-items: center; gap: 8px; }
    .filter-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-top: 8px; }
    .filter-grid label { display: grid; gap: 6px; }
    .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .dialog-grid label { display: grid; gap: 6px; }
    .dialog-grid .wide { grid-column: 1 / -1; }
    .checkbox { display: flex; gap: 8px; align-items: center; }
    .binding-summary h4 { margin: 0 0 8px; }
    .summary-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; font-size: 14px; }
    .summary-grid .wide { grid-column: 1 / -1; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) {
      .filter-grid, .dialog-grid, .summary-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class InsuranceDistributionAdminComponent implements OnInit {
  private readonly api = inject(InsuranceApiService);
  private readonly salesApi = inject(SalesApiService);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly permissionDenied = signal(false);
  readonly error = signal('');
  readonly activeTab = signal<DistributionAdminTab>('agencies');
  readonly agencies = signal<InsuranceAgencyListItem[]>([]);
  readonly producers = signal<InsuranceProducerListItem[]>([]);
  readonly territories = signal<InsuranceTerritory[]>([]);
  readonly products = signal<InsuranceProductListItem[]>([]);
  readonly salesChannels = signal<SalesChannelRecord[]>([]);
  readonly loadedBinding = signal<InsuranceChannelBinding | null>(null);
  readonly agencySearch = signal('');

  agencyDialogVisible = false;
  channelBindingDialogVisible = false;

  selectedChannelId: string | null = null;
  selectedAllowedProductIds: string[] = [];

  agencyForm: CreateInsuranceAgencyPayload = {
    legalEntityId: '',
    code: '',
    name: '',
    parentAgencyId: null,
    organizationUnitId: null
  };

  channelBindingForm: UpsertInsuranceChannelBindingPayload = {
    legalEntityId: '',
    defaultProducerId: null,
    allowedProductIdsJson: '[]',
    quoteValidityHours: 24,
    requiresUwApproval: false,
    territoryId: null,
    territoryEnforcementMode: 'None',
    customerConflictMode: 'Allow'
  };

  readonly territoryModeOptions = [
    { value: 'None', labelKey: 'insurance.distribution.territoryMode.none' },
    { value: 'Warn', labelKey: 'insurance.distribution.territoryMode.warn' },
    { value: 'Block', labelKey: 'insurance.distribution.territoryMode.block' }
  ];

  readonly customerConflictModeOptions = [
    { value: 'Allow', labelKey: 'insurance.distribution.customerConflictMode.allow' },
    { value: 'Warn', labelKey: 'insurance.distribution.customerConflictMode.warn' },
    { value: 'Block', labelKey: 'insurance.distribution.customerConflictMode.block' }
  ];

  ngOnInit(): void {
    this.loadAll();
  }

  loadAll(): void {
    this.loading.set(true);
    this.loadAgencies(false);
    this.api.listProducers().subscribe({ next: rows => this.producers.set(rows), error: () => this.producers.set([]) });
    this.api.listTerritories().subscribe({ next: rows => this.territories.set(rows), error: () => this.territories.set([]) });
    this.api.listProducts().subscribe({ next: rows => this.products.set(rows), error: () => this.products.set([]) });
    this.salesApi.listChannels().subscribe({
      next: rows => {
        this.salesChannels.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.salesChannels.set([]);
        this.loading.set(false);
      }
    });
  }

  loadAgencies(showLoading = true): void {
    if (showLoading) {
      this.loading.set(true);
    }
    this.api.listAgencies().subscribe({
      next: agencies => {
        this.agencies.set(agencies);
        this.permissionDenied.set(false);
        this.error.set('');
        if (showLoading) {
          this.loading.set(false);
        }
      },
      error: err => {
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('insurance.states.loadError'));
        if (showLoading) {
          this.loading.set(false);
        }
      }
    });
  }

  onAgencySearch(event: Event): void {
    this.agencySearch.set((event.target as HTMLInputElement).value ?? '');
  }

  filteredAgencyRows(): Array<{ code: string; displayName: string; parentName: string }> {
    const value = this.agencySearch().trim().toLowerCase();
    return this.agencyTreeRows().filter(row =>
      !value
      || row.code.toLowerCase().includes(value)
      || row.displayName.toLowerCase().includes(value)
      || row.parentName.toLowerCase().includes(value));
  }

  openAgencyDialog(): void {
    this.agencyForm = {
      legalEntityId: this.agencyForm.legalEntityId,
      code: '',
      name: '',
      parentAgencyId: null,
      organizationUnitId: null
    };
    this.agencyDialogVisible = true;
  }

  openChannelBindingDialog(): void {
    if (!this.selectedChannelId) {
      return;
    }
    if (!this.loadedBinding()) {
      this.loadChannelBinding(true);
      return;
    }
    this.channelBindingDialogVisible = true;
  }

  agencyOptions(): Array<{ label: string; value: string }> {
    return this.agencies().map(item => ({ value: item.id, label: `${item.code} - ${item.name}` }));
  }

  producerOptions(): Array<{ label: string; value: string }> {
    return this.producers().map(item => ({ value: item.id, label: `${item.code} - ${item.name}` }));
  }

  territoryOptions(): Array<{ label: string; value: string }> {
    return this.territories().map(item => ({ value: item.id, label: `${item.code} - ${item.name}` }));
  }

  productOptions(): Array<{ label: string; value: string }> {
    return this.products().map(item => ({ value: item.id, label: `${item.code} - ${item.name}` }));
  }

  channelOptions(): Array<{ label: string; value: string }> {
    return this.salesChannels().map(item => ({ value: item.id, label: `${item.code} - ${item.name}` }));
  }

  channelLabel(channelId: string | null): string {
    if (!channelId) {
      return '-';
    }
    const match = this.salesChannels().find(item => item.id === channelId);
    return match ? `${match.code} - ${match.name}` : '-';
  }

  producerLabel(producerId: string | null): string {
    if (!producerId) {
      return '-';
    }
    const match = this.producers().find(item => item.id === producerId);
    return match ? `${match.code} - ${match.name}` : '-';
  }

  territoryLabel(territoryId: string | null): string {
    if (!territoryId) {
      return '-';
    }
    const match = this.territories().find(item => item.id === territoryId);
    return match ? `${match.code} - ${match.name}` : '-';
  }

  territoryModeLabel(value: string): string {
    const match = this.territoryModeOptions.find(item => item.value === value);
    return match ? this.t(match.labelKey) : value;
  }

  customerConflictModeLabel(value: string): string {
    const match = this.customerConflictModeOptions.find(item => item.value === value);
    return match ? this.t(match.labelKey) : value;
  }

  allowedProductsSummary(allowedProductIdsJson: string): string {
    const ids = this.parseGuidArray(allowedProductIdsJson);
    if (ids.length === 0) {
      return '-';
    }
    return ids
      .map(id => {
        const match = this.products().find(item => item.id === id);
        return match ? `${match.code} - ${match.name}` : id;
      })
      .join(', ');
  }

  agencyTreeRows(): Array<{ code: string; displayName: string; parentName: string }> {
    const map = new Map(this.agencies().map(item => [item.id, item]));
    return this.agencies().map(item => ({
      code: item.code,
      displayName: `${item.parentAgencyId ? '└─ ' : ''}${item.name}`,
      parentName: item.parentAgencyId ? (map.get(item.parentAgencyId)?.name ?? '-') : '-'
    }));
  }

  saveAgency(): void {
    this.saving.set(true);
    this.api.createAgency(this.agencyForm).subscribe({
      next: created => {
        this.agencies.set([...this.agencies(), created]);
        this.agencyForm = { ...this.agencyForm, code: '', name: '' };
        this.agencyDialogVisible = false;
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  loadChannelBinding(openDialogAfterLoad = false): void {
    if (!this.selectedChannelId) {
      return;
    }
    this.api.getChannelBinding(this.selectedChannelId).subscribe({
      next: item => {
        this.loadedBinding.set(item);
        if (!item) {
          this.channelBindingForm = {
            legalEntityId: this.channelBindingForm.legalEntityId,
            defaultProducerId: null,
            allowedProductIdsJson: '[]',
            quoteValidityHours: 24,
            requiresUwApproval: false,
            territoryId: null,
            territoryEnforcementMode: 'None',
            customerConflictMode: 'Allow'
          };
          this.selectedAllowedProductIds = [];
          if (openDialogAfterLoad) {
            this.channelBindingDialogVisible = true;
          }
          return;
        }
        this.channelBindingForm = {
          legalEntityId: this.channelBindingForm.legalEntityId,
          defaultProducerId: item.defaultProducerId,
          allowedProductIdsJson: item.allowedProductIdsJson,
          quoteValidityHours: item.quoteValidityHours,
          requiresUwApproval: item.requiresUwApproval,
          territoryId: item.territoryId,
          territoryEnforcementMode: item.territoryEnforcementMode,
          customerConflictMode: item.customerConflictMode
        };
        this.selectedAllowedProductIds = this.parseGuidArray(item.allowedProductIdsJson);
        if (openDialogAfterLoad) {
          this.channelBindingDialogVisible = true;
        }
      },
      error: () => {
        this.loadedBinding.set(null);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  saveChannelBinding(): void {
    if (!this.selectedChannelId) {
      return;
    }
    this.saving.set(true);
    const payload: UpsertInsuranceChannelBindingPayload = {
      ...this.channelBindingForm,
      allowedProductIdsJson: JSON.stringify(this.selectedAllowedProductIds)
    };
    this.api.upsertChannelBinding(this.selectedChannelId, payload).subscribe({
      next: item => {
        this.loadedBinding.set(item);
        this.channelBindingDialogVisible = false;
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  private parseGuidArray(value: string): string[] {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter(item => typeof item === 'string') : [];
    } catch {
      return [];
    }
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
